import { Button } from "@/components/elements/Button";
import { TALENT_AVATAR_EXTENSION, TALENT_AVATAR_MIME_TYPES } from "@/features/talent/constants/file";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { black, button, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { FCC } from "@/types/common";
import { TExtendFileOption } from "@/types/file";
import { checkFileListWhetherIsActualPdf, convertKbToByte, getExtensionsOfMimeTypes, getFileStatusWithRules, TFileRules } from "@/utils/file";
import { makeUuid } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography, css, styled } from "@mui/material";
import { useCallback, useEffect } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

export type TAvatarUploadDropzoneProps = {
  onDragStateChange?: (isDragging: boolean) => void;
};

export const AvatarUploadDropzone: FCC<TAvatarUploadDropzoneProps> = ({ onDragStateChange }) => {
  const { id } = useParams();
  const { setValue } = useFormContext<TTalentForm>();

  const rules: TFileRules = { acceptMimeTypes: TALENT_AVATAR_MIME_TYPES, acceptFileTypesExtension: TALENT_AVATAR_EXTENSION };
  const MAX_FILE = 1;
  const onDrop = useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (acceptedFiles.length > MAX_FILE || fileRejections.length > MAX_FILE) {
      toast.error("1つのファイルしかアップロードできません。");
      return;
    }

    if (acceptedFiles.some((file) => file.size === 0)) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }

    if (fileRejections.some((file) => !rules.acceptMimeTypes?.includes(file.file.type))) {
      const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);
      toast.error(`アップロード可能なファイル形式は${extensions.join("、")}のみです。`);
      return;
    }

    const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
    if (invalidPdf) {
      toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
      return;
    }

    if (acceptedFiles[0]?.size > convertKbToByte(10000)) {
      toast.error(`ファイルサイズは10MB以内でお願いします。`);
      return;
    }

    const fileOption: TExtendFileOption = {
      id: makeUuid(),
      status: getFileStatusWithRules(acceptedFiles[0], { acceptMimeTypes: rules.acceptMimeTypes, maxSize: convertKbToByte(10000) }),
    };
    setValue("avatarFile", Object.assign(acceptedFiles[0], { ...fileOption }), { shouldDirty: true });
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: false,
    noClick: true,
    onDrop,
    accept: {
      "": TALENT_AVATAR_EXTENSION,
    },
  });

  useEffect(() => {
    onDragStateChange?.(isDragActive);
  }, [isDragActive, onDragStateChange]);

  return (
    <Box>
      <Box {...getRootProps()}>
        <input {...getInputProps()} />
        <DropZoneArea dragged={isDragActive}>
          <Stack justifyContent="center" alignItems="center" gap="16px">
            <Stack alignItems="center">
              <Typography variant="subtitle1" color={text.secondary}>
                アップロードするファイルをドラッグ&ドロップ
              </Typography>

              <Typography variant="body14" color={black[90]}>
                （ファイル形式：jpg、jpeg、png、gifに対応）
              </Typography>
            </Stack>

            <Typography variant="body14" color={text.secondary}>
              または
            </Typography>
            <Button type="button" variant="outline" size="sm" onClick={() => open()}>
              ファイルを選択する
            </Button>
          </Stack>
          <Box component="ul" sx={{ listStyle: "revert", color: text.secondary }}>
            <Typography component="li" variant="cap12" width={"100%"} mt={1}>
              10MB以下の画像ファイルをアップロードしてください。
            </Typography>
            {id && (
              <Typography component="li" variant="cap12">
                登録支援機関からのアップロードが難しい場合、下記のリンクURLを発行いただき、特定技能外国人の方にアップロードの依頼をしてください。
              </Typography>
            )}
          </Box>
        </DropZoneArea>
      </Box>
    </Box>
  );
};

const options = { shouldForwardProp: (propName: string) => !["dragged"].includes(propName) };

const DropZoneAreaActive = css`
  border-color: ${button.primary};
`;

const DropZoneArea = styled(Stack, options)<{ dragged: boolean }>`
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 24px;
  gap: 16px;
  height: 100%;
  border-radius: ${rounded.sm};
  overflow: hidden;

  ${({ dragged }) => dragged && DropZoneAreaActive}
`;
